import React, { useState } from 'react';
import { CheckIcon, SparklesIcon, ClockIcon } from '@heroicons/react/24/outline';
import { loadStripe } from '@stripe/stripe-js';
import { subscriptionService } from '../services/subscriptionService';
import toast from 'react-hot-toast';
import { useSubscription } from '../contexts/SubscriptionContext';
import PaymentMethodModal from '../components/PaymentMethodModal';
import { isAndroid } from '../utils/deviceDetection';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51S2BtTJB5DzW93CyOJJqivJRytIzrYI723cRnRx0ebZd1MfxV3QSMlSqjgVkJItTHO6hkdpVcc1OaGKcdhiSLTLT00TEBZ4XNA');

type BillingPeriod = 'monthly' | 'annual';

const Pricing: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const { subscription, refreshSubscription } = useSubscription();
  const isAndroidDevice = isAndroid();

  const handleUpgrade = async () => {
    try {
      setLoading(true);
      
      // Get the appropriate price ID based on billing period
      const priceId = billingPeriod === 'annual' 
        ? process.env.REACT_APP_STRIPE_PRICE_ID_ANNUAL || 'price_1U14gVJMNA50hfYcEIgFa0WL'
        : process.env.REACT_APP_STRIPE_PRICE_ID || 'price_1U14gVJMNA50hfYcVLdcxIkj';
      // Create checkout session
      const { url } = await subscriptionService.createCheckoutSession({
        priceId,
        successUrl: `${window.location.origin}/decks?success=true`,
        cancelUrl: `${window.location.origin}/pricing?canceled=true`,
      });

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Falha ao iniciar processo de checkout');
    } finally {
      setLoading(false);
    }
  };

  const handleOneDayPass = () => {
    setShowPaymentModal(true);
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Tem certeza de que deseja cancelar sua assinatura?')) {
      return;
    }

    try {
      setLoading(true);
      await subscriptionService.cancelSubscription();
      await refreshSubscription();
      toast.success('Assinatura cancelada com sucesso');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Falha ao cancelar assinatura');
    } finally {
      setLoading(false);
    }
  };

  const freeFeatures = [
    '🤖 Criação de estudos com IA (20 Flashcards por Deck, 15 questões por Quizz e Matches ilimitados)',
    '🃏 3 Decks de flashcards - 100 cards por deck (300 total) + Modo de estudo básico',
    '📝 3 Quizzes - 15 questões por quizz',
    '🎯 3 Matches - pares ilimitados',
    '📚 Módulo de Estudos: Crie estudos completos com IA (3 módulos)',
    '📊 Acompanhamento de progresso',
    
  ];

  const oneDayPassFeatures = [
    '⏱️ Acesso Premium por 24 horas',
  ];

  const premiumFeatures = [
    '🤖 Criação de estudos com IA (500 Flashcards por Deck, 50 questões por Quizz e Pares Ilimitados por Matches)',
    '🃏 Decks de flashcards ilimitados + Modo de estudo rápido',
    '📝 Quizzes ilimitados',
    '🎯 Matches ilimitados',
    '📚 Módulo de Estudos: Crie estudos completos com IA ilimitados',
    '📊 Análises detalhadas',
    '💾 Funcionalidade de exportar/importar',
    '📈 Estatísticas avançadas',
    '⭐ Suporte prioritário',
  ];

  return (
    <>
      {/* Payment Method Modal */}
      <PaymentMethodModal 
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        pixCode="00020126580014br.gov.bcb.pix01360300e793-8b58-41ff-be85-65af61c651c2520400005303986540515.905802BR5901N6001C62170513upmindydayuse6304F74F"
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
            Escolha Seu Plano
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
            Comece a aprender gratuitamente, faça upgrade quando precisar de mais
          </p>
        </div>

        {/* Billing Period Toggle - Always shown */}
        <div className="mt-8 flex justify-center">
          <div className="relative bg-gray-100 dark:bg-gray-800 rounded-lg p-1 flex items-center space-x-1">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`relative px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                billingPeriod === 'monthly'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`relative px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                billingPeriod === 'annual'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Anual
              <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                Economize 20%
              </span>
            </button>
          </div>
        </div>

        <div className="mt-12 space-y-4 sm:mt-16 sm:space-y-0 sm:grid sm:gap-6 lg:mx-auto sm:grid-cols-3 lg:max-w-5xl">
          {/* Free Plan */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            <div className="p-6">
              <h2 className="text-2xl leading-6 font-semibold text-gray-900 dark:text-white">Gratuito</h2>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Perfeito para começar
              </p>
              <p className="mt-8">
                <span className="text-4xl font-extrabold text-gray-900 dark:text-white">R$ 0</span>
                <span className="text-base font-medium text-gray-500 dark:text-gray-400">/mês</span>
              </p>
              {subscription?.subscriptionTier === 'free' && (
                <div className="mt-4 p-3 bg-green-100 dark:bg-green-900/20 rounded-md">
                  <p className="text-sm text-green-800 dark:text-green-200 font-medium">Plano Atual</p>
                </div>
              )}
            </div>
            <div className="pt-6 pb-8 px-6">
              <h3 className="text-xs font-medium text-gray-900 dark:text-white uppercase tracking-wide">
                O que está incluído
              </h3>
              <ul className="mt-6 space-y-4">
                {freeFeatures.map((feature) => (
                  <li key={feature} className="flex space-x-3">
                    <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* One Day Pass */}
          <div className="border border-orange-200 dark:border-orange-700 rounded-lg shadow-sm divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 relative">
            <div className="absolute top-0 right-6 transform -translate-y-1/2">
              <span className="inline-flex items-center px-4 py-1 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                <ClockIcon className="w-3 h-3 mr-1" />
                Acesso Rápido
              </span>
            </div>
            <div className="p-6">
              <h2 className="text-2xl leading-6 font-semibold text-gray-900 dark:text-white">Passe de 1 Dia</h2>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Acesso total por 24 horas
              </p>
              <p className="mt-8">
                <span className="text-4xl font-extrabold text-gray-900 dark:text-white">R$ 15,90</span>
                <span className="text-base font-medium text-gray-500 dark:text-gray-400">/dia</span>
              </p>
              <div className="mt-6">
                <button
                  onClick={handleOneDayPass}
                  disabled={loading || subscription?.subscriptionTier === 'premium'}
                  className="w-full bg-orange-600 border border-transparent rounded-md py-2 px-4 flex items-center justify-center text-sm font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processando...' : 'Comprar Passe de 1 Dia'}
                </button>
              </div>
            </div>
            <div className="pt-6 pb-8 px-6">
              <h3 className="text-xs font-medium text-gray-900 dark:text-white uppercase tracking-wide">
                O que está incluído
              </h3>
              <ul className="mt-6 space-y-4">
                {oneDayPassFeatures.map((feature) => (
                  <li key={feature} className="flex space-x-3">
                    <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Premium Plan - Always shown */}
          <div className="border border-primary-200 dark:border-primary-700 rounded-lg shadow-sm divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 relative">
            <div className="absolute top-0 right-6 transform -translate-y-1/2">
              <span className="inline-flex items-center px-4 py-1 rounded-full text-xs font-semibold bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200">
                <SparklesIcon className="w-3 h-3 mr-1" />
                Mais Popular
              </span>
            </div>
            <div className="p-6">
              <h2 className="text-2xl leading-6 font-semibold text-gray-900 dark:text-white">Premium</h2>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Desbloqueie o potencial ilimitado de aprendizagem
              </p>
              <p className="mt-8">
                {billingPeriod === 'annual' ? (
                  <>
                    <span className="text-4xl font-extrabold text-gray-900 dark:text-white">R$ 24,92</span>
                    <span className="text-base font-medium text-gray-500 dark:text-gray-400">/mês</span>
                    <div className="mt-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Cobrado R$ 299,04 anualmente
                      </span>
                    </div>
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        Economize R$ 299,76 por ano
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-extrabold text-gray-900 dark:text-white">R$ 49,90</span>
                    <span className="text-base font-medium text-gray-500 dark:text-gray-400">/mês</span>
                  </>
                )}
              </p>
              <div className="mt-6">
                {subscription?.subscriptionTier === 'premium' && subscription?.isSubscriptionActive ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-md">
                      <p className="text-sm text-green-800 dark:text-green-200 font-medium">Plano Atual</p>
                      {subscription.subscriptionExpiresAt && (
                        <p className="text-xs text-green-600 dark:text-green-300 mt-1">
                          Renova em {new Date(subscription.subscriptionExpiresAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={handleCancelSubscription}
                      disabled={loading}
                      className="w-full bg-red-600 border border-transparent rounded-md py-2 px-4 flex items-center justify-center text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Processando...' : 'Cancelar Assinatura'}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleUpgrade}
                    disabled={loading}
                    className="w-full bg-primary-600 border border-transparent rounded-md py-2 px-4 flex items-center justify-center text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Processando...' : 'Fazer Upgrade para Premium'}
                  </button>
                )}
              </div>
            </div>
            <div className="pt-6 pb-8 px-6">
              <h3 className="text-xs font-medium text-gray-900 dark:text-white uppercase tracking-wide">
                O que está incluído
              </h3>
              <ul className="mt-6 space-y-4">
                {premiumFeatures.map((feature) => (
                  <li key={feature} className="flex space-x-3">
                    <CheckIcon className="flex-shrink-0 h-5 w-5 text-green-500" aria-hidden="true" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

      </div>
    </div>
    </>
  );
};

export default Pricing;
